import { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "@/firebase";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();

      const res = await axios.post(`${API}/auth/firebase/admin-login`, { id_token: idToken });
      localStorage.setItem("hafani_admin_token", res.data.access_token);
      localStorage.setItem("hafani_admin_role", res.data.role);

      if (res.data.role === "super_admin") {
        navigate("/admin/super");
      } else {
        navigate("/app/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === "auth/user-not-found" || err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        toast.error("Email ou mot de passe incorrect.");
      } else if (detail) {
        toast.error(detail);
      } else {
        toast.error("Erreur de connexion. Vérifiez vos identifiants.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex flex-col items-start group">
          <img
            src="/assets/logo.png"
            alt="Hafani"
            className="h-9 w-auto object-contain transition-transform group-hover:scale-105"
          />
          <span className="text-[9px] text-emerald-700 font-medium mt-0.5 tracking-wide">
            Hafani, une solution pensée pour notre fin
          </span>
        </Link>
        <Link
          to="/"
          className="text-sm text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1 group"
        >
          <span className="transition-transform group-hover:-translate-x-0.5">←</span> Accueil
        </Link>
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Wordmark */}
          <div className="text-center mb-10">
            <p className="text-xs font-medium tracking-[0.2em] text-slate-500 uppercase mb-2">
              Hafani MS
            </p>
            <h1
              className="text-4xl text-slate-900"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            >
              Espace Administrateur
            </h1>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 px-8 py-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-1.5">
                <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
                  Adresse email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="admin@hafani-ms.com"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
                    Mot de passe
                  </label>
                  <Link
                    to="/admin/forgot-password"
                    className="text-xs text-emerald-600 hover:text-emerald-700 transition-colors"
                  >
                    Mot de passe oublié ?
                  </Link>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              >
                {loading ? "Connexion…" : "Se connecter"}
              </button>
            </form>
          </div>

          <div className="text-center mt-6 space-y-1.5">
            <p className="text-xs text-slate-400">
              Première connexion ? Consultez votre email d'invitation.
            </p>
            <p className="text-xs text-slate-400">
              Pas encore de compte ?{" "}
              <Link to="/admin/register" className="text-emerald-600 hover:text-emerald-700 transition-colors">
                Enregistrer votre association
              </Link>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center">
        <p className="text-xs text-slate-400 font-light">
          &copy; {new Date().getFullYear()} Hafani Management and Solutions
        </p>
      </footer>
    </div>
  );
};
